import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import QRCode from "qrcode";
import {
  useGetTotpSetupDataQuery,
  useEnableTotpMutation,
  useDisableTotpMutation,
} from "../store/slices/settingsSlice";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldAlt } from "@fortawesome/free-solid-svg-icons";

// mode: "idle" | "setup" | "disable"

function TotpSection({ totpEnabled, onStatusChange }) {
  const [mode, setMode] = useState("idle");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const canvasRef = useRef(null);

  const { data: setupData, isFetching } = useGetTotpSetupDataQuery(undefined, {
    skip: mode !== "setup",
  });

  const [enableTotp, { isLoading: enabling }] = useEnableTotpMutation();
  const [disableTotp, { isLoading: disabling }] = useDisableTotpMutation();

  // Render QR code onto canvas whenever setup data arrives
  useEffect(() => {
    if (mode === "setup" && setupData?.qr_uri && canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, setupData.qr_uri, {
        width: 200,
        margin: 2,
        color: { dark: "#000000", light: "#ffffff" },
      });
    }
  }, [mode, setupData]);

  const reset = () => {
    setMode("idle");
    setCode("");
    setError("");
  };

  const copyToClipboard = async (value) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      toast.success("Copied to clipboard");
    } catch (err) {
      toast.error("Could not copy to clipboard");
    }
  };

  const handleEnable = async () => {
    setError("");
    if (code.replace(/\s/g, "").length !== 6) {
      setError("Please enter the 6-digit code from your authenticator app.");
      return;
    }
    const { data, error: err } = await enableTotp({
      code: code.replace(/\s/g, ""),
    });
    if (err || data?.status === "error") {
      setError(
        err?.data?.message || data?.message || "Invalid code. Please try again."
      );
      return;
    }
    toast.success("Authenticator app enabled successfully.");
    reset();
    onStatusChange(true);
  };

  const handleDisable = async () => {
    setError("");
    if (code.replace(/\s/g, "").length !== 6) {
      setError("Please enter the 6-digit code from your authenticator app.");
      return;
    }
    const { data, error: err } = await disableTotp({
      code: code.replace(/\s/g, ""),
    });
    if (err || data?.status === "error") {
      setError(
        err?.data?.message || data?.message || "Invalid code. Please try again."
      );
      return;
    }
    toast.success("Authenticator app disabled.");
    reset();
    onStatusChange(false);
  };

  // ── Setup flow ──────────────────────────────────────────────────────────────
  if (mode === "setup") {
    return (
      <div className="bg-white rounded-3 p-3 my-2">
        <div className="fw-bold mb-1 d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faShieldAlt} />
          Set Up Authenticator App
        </div>
        <p className="text-secondary small mb-3">
          Scan the QR code with <strong>Google Authenticator</strong>,{" "}
          <strong>Microsoft Authenticator</strong>, or <strong>Authy</strong>,
          then enter the 6-digit code to confirm.
        </p>

        <div className="d-flex align-items-center justify-content-center">
          <div className="">
            {isFetching ? (
              <Spinner animation="border" />
            ) : (
              <div
                style={{
                  // display: "inline-block",
                  background: "#fff",
                  padding: 12,
                  borderRadius: 8,
                  border: "1px solid #dee2e6",
                }}
              >
                <canvas ref={canvasRef} />
              </div>
            )}
          </div>
          <div>
            {setupData?.secret && (
              <div className="bg-light rounded-2 p-2 m-3 small text-center">
                <div className="mb-1">
                  Will appear in your app as:{" "}
                  <strong>
                    {setupData.issuer} ({setupData.email})
                  </strong>
                </div>
                <div className="mb-1">Can't scan? Enter key manually:</div>
                <code
                  className="user-select-all"
                  style={{ fontSize: "0.8rem", letterSpacing: "0.15rem" }}
                  onClick={() => copyToClipboard(setupData.secret)}
                >
                  {setupData.secret}
                </code>
              </div>
            )}
            <Form.Group className="mx-3 mb-1">
              <Form.Control
                placeholder="6-digit code"
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => e.key === "Enter" && handleEnable()}
              />
            </Form.Group>
            {error && (
              <div className="alert alert-danger p-2 small mx-3">{error}</div>
            )}
          </div>
        </div>

        <div className="d-flex gap-2 mt-2 justify-content-end">
          <Button
            variant="colored"
            className="rounded-pill"
            onClick={handleEnable}
            disabled={enabling || isFetching}
          >
            {enabling ? <Spinner size="sm" animation="border" /> : "Enable"}
          </Button>
          <Button
            variant="outline-secondary"
            className="rounded-pill"
            onClick={reset}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // ── Disable flow ─────────────────────────────────────────────────────────────
  if (mode === "disable") {
    return (
      <div className="bg-white rounded-3 p-3 my-2">
        <div className="fw-bold mb-1 d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faShieldAlt} />
          Disable Authenticator App
        </div>
        <p className="text-secondary small mb-3">
          Enter your current 6-digit code to confirm you want to disable
          two-factor authentication.
        </p>

        <Form.Group className="mb-2">
          <Form.Control
            placeholder="6-digit code"
            maxLength={6}
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && handleDisable()}
          />
        </Form.Group>

        {error && (
          <div className="alert alert-danger p-2 small mb-2">{error}</div>
        )}

        <div className="d-flex gap-2 mt-2 justify-content-end">
          <Button
            variant="danger"
            className="rounded-pill"
            onClick={handleDisable}
            disabled={disabling}
          >
            {disabling ? <Spinner size="sm" animation="border" /> : "Disable"}
          </Button>
          <Button
            variant="outline-secondary"
            className="rounded-pill"
            onClick={reset}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // ── Idle / status row ────────────────────────────────────────────────────────
  return (
    <div className="d-flex align-items-center justify-content-between bg-white rounded-3 p-3 my-2">
      <div>
        <div className="fw-bold mb-1 d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faShieldAlt} />
          Authenticator App
          {totpEnabled ? (
            <span className="badge bg-success px-3 py-1">Enabled</span>
          ) : (
            <span className="badge bg-secondary px-3 py-1">Not set up</span>
          )}
        </div>
        <div className="small text-secondary">
          {totpEnabled
            ? "Two-factor authentication via authenticator app is active."
            : "Add an extra layer of security to your account."}
        </div>
      </div>

      <div className="d-flex align-items-center gap-2 ms-3 flex-shrink-0">
        {totpEnabled ? (
          <Button
            variant="outline-danger"
            size="sm"
            className="rounded-pill"
            onClick={() => {
              setCode("");
              setError("");
              setMode("disable");
            }}
          >
            Disable
          </Button>
        ) : (
          <Button
            variant="colored"
            size="sm"
            className="rounded-3 fw-bold"
            onClick={() => {
              setCode("");
              setError("");
              setMode("setup");
            }}
          >
            Set Up
          </Button>
        )}
      </div>
    </div>
  );
}

export default TotpSection;
